import React, { useMemo, useState } from "react";

import { Box, Container, Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Input, Select } from "../../../../../../../components/Form";
import Button, { Color } from "../../../../../../../components/Form/Button";
import MapInput from "../../../../../../../components/Form/MapInput";
import PhoneInput from "../../../../../../../components/Form/PhoneInput";
import { AccordionStep } from "../../../../../../../components/Generic/AccordionStep";
import SkeletonComponent from "../../../../../../../components/Generic/Skeleton";
import useYupValidation from "../../../../../../../hooks/useYupValidation";
import { Location } from "../../../../../../../models/location";
import { Member } from "../../../../../../../models/members/member";
import { Subcontractor, SubcontractorAddresses } from "../../../../../../../models/subcontractor";
import { BreadcrumbKit } from "../Breadcrumb";
import { useSubcontractorActions } from "../hooks/useActions";
import { MembersStep } from "./components/MembersStep";
import useStyles, {
  AccordionDetailsWrapper,
  BillingAddressContainer,
  FieldContainer,
} from "./styles";

const CreateSubContractor = () => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [fields, setFields] = useState<Partial<Subcontractor>>({});
  const [membersAdded, setMembersAdded] = useState<Member[]>([]);

  const { useCreateSubcontractor, onBackToSubcontractorsTab, getLanguagesAvailable } =
    useSubcontractorActions();
  const { mutate: createSubcontractor, isLoading: isLoadingCreateSubcontractor } =
    useCreateSubcontractor(onBackToSubcontractorsTab);

  const { data: languageList, isFetching: isLoadingLanguages } = getLanguagesAvailable();

  const onSetField = (key: keyof Subcontractor, value: any) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const onChangeField = (key: keyof Subcontractor, event: React.ChangeEvent<any>) => {
    if (event && event.target) {
      setFields((prev) => ({ ...prev, [key]: event.target.value }));
    }
  };

  const onSetAddressField = (
    objectKey: keyof SubcontractorAddresses,
    propertyKey: keyof Location,
    value: any
  ) => {
    setFields((prev) => {
      const get = prev[objectKey] || {};

      get[propertyKey] = value;

      return { ...prev, [objectKey]: get };
    });
  };

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const { fieldHasError, getErrorByField } = useYupValidation<Subcontractor>(errors);

  const schema = yup.object().shape({
    businessName: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    businessEmail: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    firstName: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    lastName: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    contactPhone: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    contactEmail: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
  });

  const errorsOnStepOne = useMemo(() => {
    return fieldHasError("businessName") || fieldHasError("businessEmail");
  }, [errors]);

  const errorsOnStepTwo = useMemo(() => {
    return (
      fieldHasError("firstName") ||
      fieldHasError("lastName") ||
      fieldHasError("contactPhone") ||
      fieldHasError("contactEmail")
    );
  }, [errors]);

  const onValidateFields = () => {
    try {
      schema.validateSync(fields, { abortEarly: false });
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error.inner);
      }

      return false;
    }
  };

  const onSubmit = () => {
    if (onValidateFields()) {
      createSubcontractor({
        data: {
          ...fields,
          members: membersAdded || [],
        } as Subcontractor,
      });
    }
  };

  return (
    <>
      <BreadcrumbKit
        items={[
          {
            key: "createSubcontractor",
            label: translate("createNew"),
          },
        ]}
      />
      <Container className={classes.container}>
        <AccordionStep.Root hasError={errorsOnStepOne} defaultExpanded>
          <AccordionStep.Header
            title={translate("businessDetails")}
            step={1}
            subtitle={translate("businessInformationAboutTheSubcontractor")}
            isMandatory
            hasError={errorsOnStepOne}
          />
          <AccordionDetailsWrapper>
            <AccordionStep.Divider />
            <Typography className={classes.formHeading}>{translate("details")}</Typography>
            <FieldContainer container spacing={3}>
              <Grid item xs={4}>
                <Input
                  required
                  name="businessName"
                  label={translate("businessName")}
                  value={fields.businessName}
                  onChange={(event) => onChangeField("businessName", event)}
                  placeholder={translate("typeHere")}
                  error={fieldHasError("businessName")}
                  errorMessage={getErrorByField("businessName")}
                />
              </Grid>
              <Grid item xs={4}>
                <PhoneInput
                  name="BusinessPhone"
                  defaultCountry="AU"
                  international
                  placeholder="61430111900"
                  label={translate("businessPhone")}
                  error={!!getErrorByField("businessPhone")}
                  errorMessage={getErrorByField("businessPhone")}
                  value={fields.businessPhone}
                  onChange={(value) => onSetField("businessPhone", value)}
                />
              </Grid>

              <Grid item xs={4}>
                <Input
                  required
                  name="businessEmail"
                  label={translate("mainEmailAddress")}
                  value={fields.businessEmail}
                  onChange={(event) => onChangeField("businessEmail", event)}
                  placeholder={translate("typeHere")}
                  error={fieldHasError("businessEmail")}
                  errorMessage={getErrorByField("businessEmail")}
                />
              </Grid>
            </FieldContainer>
            <Typography className={classes.formHeading}>{translate("businessAddress")}</Typography>
            <FieldContainer container spacing={3}>
              <Grid item xs={8}>
                <MapInput
                  location={fields?.businessLocation}
                  onChange={(location) => {
                    onSetField("businessLocation", location);
                  }}
                  label={translate("registredAddress")}
                  onChangeInput={(value) => {
                    onSetAddressField("businessLocation", "longAddress", value);
                  }}
                  inputValue={fields?.businessLocation?.longAddress}
                  placeholder={translate("searchAddress")}
                  data-cy={`business-address`}
                />
              </Grid>
            </FieldContainer>
          </AccordionDetailsWrapper>
        </AccordionStep.Root>

        <AccordionStep.Root hasError={errorsOnStepTwo}>
          <AccordionStep.Header
            title={translate("contactDetails")}
            step={2}
            subtitle={translate("generalInformationAboutTheOwner")}
            isMandatory
            hasError={errorsOnStepTwo}
          />
          <AccordionDetailsWrapper>
            <AccordionStep.Divider />

            <Typography className={classes.formHeading}>{translate("details")}</Typography>
            <FieldContainer container spacing={3}>
              <Grid item xs={4}>
                <Input
                  required
                  name="firstName"
                  label={translate("firstName")}
                  value={fields.firstName}
                  onChange={(event) => onChangeField("firstName", event)}
                  placeholder={translate("typeHere")}
                  error={fieldHasError("firstName")}
                  errorMessage={getErrorByField("firstName")}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  required
                  name="lastName"
                  label={translate("lastName")}
                  value={fields.lastName}
                  onChange={(event) => onChangeField("lastName", event)}
                  placeholder={translate("typeHere")}
                  error={fieldHasError("lastName")}
                  errorMessage={getErrorByField("lastName")}
                />
              </Grid>
              <Grid item xs={4}>
                <PhoneInput
                  required
                  name="Phone"
                  defaultCountry="AU"
                  international
                  placeholder="61430111900"
                  label={translate("phone")}
                  error={fieldHasError("contactPhone")}
                  errorMessage={getErrorByField("contactPhone")}
                  value={fields.contactPhone}
                  onChange={(value) => onSetField("contactPhone", value)}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  required
                  name="contactEmail"
                  label={translate("email")}
                  value={fields.contactEmail}
                  onChange={(event) => onChangeField("contactEmail", event)}
                  placeholder={translate("typeHere")}
                  error={fieldHasError("contactEmail")}
                  errorMessage={getErrorByField("contactEmail")}
                />
              </Grid>
              <Grid item xs={4}>
                <SkeletonComponent isLoading={isLoadingLanguages} isInput>
                  <Select
                    label={translate("language")}
                    value={fields.contactLanguageId}
                    onChange={(event: any) => onChangeField("contactLanguageId", event)}
                  >
                    {(languageList || []).map((language, index) => (
                      <MenuItem value={language.id} key={index}>
                        {language.name}
                      </MenuItem>
                    ))}
                  </Select>
                </SkeletonComponent>
              </Grid>
              <Grid item xs={8}>
                <MapInput
                  location={fields?.contactLocation}
                  onChange={(location) => {
                    onSetField("contactLocation", location);
                  }}
                  label={translate("personalAddress")}
                  onChangeInput={(value) => {
                    onSetAddressField("contactLocation", "longAddress", value);
                  }}
                  inputValue={fields?.contactLocation?.longAddress}
                  placeholder={translate("searchAddress")}
                  data-cy={`contact-address`}
                />
              </Grid>
            </FieldContainer>

            <Typography className={classes.formHeading}>{translate("bankingDetails")}</Typography>
            <FieldContainer container spacing={3}>
              <Grid item xs={4}>
                <Input
                  name="vatCode"
                  label={translate("vatCode")}
                  value={fields.vatCode}
                  onChange={(event) => onChangeField("vatCode", event)}
                  placeholder={translate("addNumberHere")}
                  error={fieldHasError("vatCode")}
                  errorMessage={getErrorByField("vatCode")}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  name="iban"
                  label={translate("iban")}
                  value={fields.iban}
                  onChange={(event) => onChangeField("iban", event)}
                  placeholder={translate("addNumberHere")}
                  error={fieldHasError("iban")}
                  errorMessage={getErrorByField("iban")}
                />
              </Grid>
              <Grid item xs={4}>
                <Input
                  name="bic"
                  label={translate("bic")}
                  value={fields.bic}
                  onChange={(event) => onChangeField("bic", event)}
                  placeholder={translate("addNumberHere")}
                  error={fieldHasError("bic")}
                  errorMessage={getErrorByField("bic")}
                />
              </Grid>
              <BillingAddressContainer item xs={8}>
                <MapInput
                  location={fields?.billingLocation}
                  onChange={(location) => {
                    onSetField("billingLocation", location);
                  }}
                  label={translate("address")}
                  onChangeInput={(value) => {
                    onSetAddressField("billingLocation", "longAddress", value);
                  }}
                  inputValue={fields?.billingLocation?.longAddress}
                  placeholder={translate("searchAddress")}
                  data-cy={`contact-address`}
                />
              </BillingAddressContainer>
            </FieldContainer>
          </AccordionDetailsWrapper>
        </AccordionStep.Root>

        <AccordionStep.Root>
          <AccordionStep.Header
            title={translate("members")}
            step={3}
            subtitle=""
            isMandatory={false}
          />
          <AccordionDetailsWrapper>
            <AccordionStep.Divider />
            <MembersStep membersAdded={membersAdded} setMembersAdded={setMembersAdded} />
          </AccordionDetailsWrapper>
        </AccordionStep.Root>

        <Box className={classes.buttonOptions}>
          <Button
            customColor={Color.CANCEL}
            disabled={isLoadingCreateSubcontractor}
            onClick={onBackToSubcontractorsTab}
          >
            {translate("cancel")}
          </Button>
          <Button
            customColor={Color.SUCCESS}
            disabled={isLoadingCreateSubcontractor}
            onClick={onSubmit}
            loading={isLoadingCreateSubcontractor}
          >
            {translate("create")}
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default CreateSubContractor;
